import { Component } from '@theme/component';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { morphSection, sectionRenderer } from '@theme/section-renderer';

/**
 * The "Recently viewed" row (sections/recently-viewed.liquid).
 *
 * Reads the product ids Horizon already keeps in this browser (localStorage "viewedProducts",
 * assets/recently-viewed-products.js), leaves out the product on screen, and asks the store's own search page
 * to draw this section for those ids (Section Rendering API): one GET to /search on this store, nothing else
 * leaves the browser. The section stays hidden when there is nothing to show, when storage is blocked, and
 * when the request fails.
 *
 * @extends {Component}
 */
class RecentlyViewedProducts extends Component {
  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {AbortController | null} */
  #activeFetch = null;

  #started = false;

  connectedCallback() {
    super.connectedCallback();
    if (this.#started) return;

    // A hidden element never intersects the viewport, so watch its section wrapper instead.
    const target = this.parentElement ?? this;
    if (!('IntersectionObserver' in window)) {
      this.#start();
      return;
    }
    this.#observer?.disconnect();
    this.#observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        this.#start();
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.#observer.observe(target);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    this.#observer = null;
    this.#activeFetch?.abort();
    this.#activeFetch = null;
  }

  /**
   * The ids to show: valid product ids only, no repeats, not the product on screen, at most the section's limit.
   * @returns {string[]}
   */
  get productIds() {
    const { productId = '' } = this.dataset;
    const limit = Math.max(1, Number.parseInt(this.dataset.limit ?? '', 10) || 4);

    /** @type {unknown} */
    let stored = [];
    try {
      stored = RecentlyViewed.getProducts();
    } catch {
      return [];
    }
    if (!Array.isArray(stored)) return [];

    /** @type {string[]} */
    const ids = [];
    for (const value of stored) {
      const id = String(value);
      if (!/^\d+$/.test(id) || id === productId || ids.includes(id)) continue;
      ids.push(id);
      if (ids.length >= limit) break;
    }
    return ids;
  }

  #start() {
    if (this.#started) return;
    this.#started = true;
    this.#load().catch((error) => this.#fail(error));
  }

  async #load() {
    const { sectionId } = this.dataset;
    if (!sectionId) return;

    const ids = this.productIds;
    if (ids.length === 0) return;

    // Theme.routes.search_url is the store's own /search (with the market or language prefix, when there is one).
    const url = new URL(Theme.routes.search_url, window.location.origin);
    url.searchParams.set('q', ids.map((id) => `id:${id}`).join(' OR '));
    url.searchParams.set('type', 'product');
    url.searchParams.set('options[prefix]', 'none');

    this.#activeFetch?.abort();
    const controller = new AbortController();
    this.#activeFetch = controller;

    try {
      const html = await sectionRenderer.getSectionHTML(sectionId, false, url, controller.signal);
      if (controller.signal.aborted) return;
      await morphSection(sectionId, html, { mode: 'hydration', injectStylesheet: true });
    } finally {
      if (this.#activeFetch === controller) this.#activeFetch = null;
    }

    if (this.querySelector('.resource-list__item')) this.hidden = false;
  }

  /**
   * @param {unknown} error
   */
  #fail(error) {
    if (error instanceof DOMException && error.name === 'AbortError') return;
    // The theme editor places sections the Section Rendering API cannot draw yet; that is not an error.
    if (!Shopify.designMode) console.warn('Recently viewed products could not be shown:', error);
  }
}

if (!customElements.get('recently-viewed-products')) {
  customElements.define('recently-viewed-products', RecentlyViewedProducts);
}
